'use client'
import * as ReactLeaflet from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import "leaflet-defaulticon-compatibility"
import "leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css"
import MapMarker from "@/components/ContentElements/Map/MapMarker";
import Container from "@/components/ContentElements/Grid/Container";
import Column from "@/components/ContentElements/Grid/Column";
import IconRenderer from "@/libs/contentful/helper/IconRenderer";
import Headline from "@/components/typographie/Headlines/Headline";
import React, {useState} from "react";
import {LatLngBoundsExpression} from 'leaflet';
import MarkerClusterGroup from "react-leaflet-markercluster";
import 'react-leaflet-markercluster/styles'
import {useTranslations} from "next-intl";
import {Link} from "@/libs/localeHandling/routing";

const {MapContainer, TileLayer} = ReactLeaflet;

interface Props {
    mapMarkers: MapMarkerType[]
}

export type MapMarkerType = {
    fields: {
        headline: string
        icon: string,
        coordinates: {
            lat: number,
            lon: number
        }
        company: string
        companyAddress: string,
        companySize: string,
        cooperation: string,
        certificate: {
            fields?: {
                url: string,
                title: string
            }
        }
    }
}

const DynamicMap = ({mapMarkers}: Props) => {
    const t = useTranslations('LeafletMap');
    const [dialogIndex, setDialogIndex] = useState(0)
    const toggleDialog = (id: string) => {
        const dialog = (document.getElementById(id) as HTMLDialogElement)
        if (!dialog.open) {
            dialog.showModal()
        } else {
            dialog.close()
        }
    }

    const bounds: LatLngBoundsExpression = []
    mapMarkers.map((mapMarker, index) => {
        bounds.push([mapMarker.fields.coordinates.lat, mapMarker.fields.coordinates.lon]);
    })

    const renderIcon = (icon: string) => {
        let returnIcon = "icon-location-pin";
        if (icon) {
            switch (icon) {
                case 'Apparel':
                    returnIcon = 'icon-hanger'

            }
        }

        return <IconRenderer icon={returnIcon} className="" FillColor="black"/>;
    }

    const calculateCooperationYear = (cooperationDate: string) => {
        // @ts-ignore
        const date = new Date(new Date() - new Date(cooperationDate)).getFullYear() - 1970
        // @ts-ignore
        switch (date) {
            case 0:
                return t('cooperation_current')
            case -1:
                return t('cooperation_future')
            default:
                return t('cooperation_since', {age: date})
        }
    }


    return (
        <Container>
            <Column width="1">
                <MapContainer className="w-full h-full z-0 aspect-video" bounds={bounds}>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MarkerClusterGroup
                        chunkedLoading
                    >
                        {mapMarkers.map((mapMarker, index) => (
                            <MapMarker key={index} latitude={mapMarker.fields.coordinates.lat} longitude={mapMarker.fields.coordinates.lon}
                                       icon={mapMarker.fields.icon} toggleDialog={toggleDialog} index={index} setDialogIndex={setDialogIndex}/>
                        ))}
                    </MarkerClusterGroup>
                </MapContainer>
                <dialog id="mapDialog"
                        className="w-[calc(100dvw-theme(spacing.2xl))] max-w-[600px] h-[calc(100dvh-theme(spacing.3xl))] max-h-[400px] p-xl backdrop:bg-brand-blue/80 z-[401]">
                    <button onClick={() => toggleDialog('mapDialog')} className="absolute right-[15px] top-[15px]"><IconRenderer icon="icon-close"
                                                                                                                                 className="w-[16px] h-[16px]"/>
                    </button>
                    {mapMarkers[dialogIndex].fields.headline &&
                        <Headline type="h2">{mapMarkers[dialogIndex].fields.headline}</Headline>
                    }

                    {mapMarkers[dialogIndex].fields.icon &&
                        <div className="flex mb-s mt-m">
                            {mapMarkers[dialogIndex].fields.icon && renderIcon(mapMarkers[dialogIndex].fields.icon)}
                            <p className="ml-s">{mapMarkers[dialogIndex].fields.icon}</p>
                        </div>
                    }
                    {mapMarkers[dialogIndex].fields.company &&
                        <div className="flex mb-s mt-m">
                            <IconRenderer icon="icon-factory" className="" FillColor="black"/>
                            <p className="ml-s">{mapMarkers[dialogIndex].fields.company}</p>
                        </div>
                    }
                    {mapMarkers[dialogIndex].fields.companyAddress &&
                        <div className="flex mb-s mt-m">
                            <IconRenderer icon="icon-location-pin" className="" FillColor="black"/>
                            <p className="ml-s">{mapMarkers[dialogIndex].fields.companyAddress}</p>
                        </div>
                    }
                    {mapMarkers[dialogIndex].fields.companySize &&
                        <div className="flex mb-s mt-m">
                            <IconRenderer icon="icon-user-three" className="" FillColor="black"/>
                            <p className="ml-s">{t('companySize', {size: mapMarkers[dialogIndex].fields.companySize})}</p>
                        </div>
                    }

                    {mapMarkers[dialogIndex].fields.certificate?.fields?.url &&
                        <div className="flex mb-s mt-m">
                            <IconRenderer icon="icon-badge" className="" FillColor="black"/>
                            <Link href={mapMarkers[dialogIndex].fields.certificate.fields.url} className="ml-s underline">
                                {mapMarkers[dialogIndex].fields.certificate.fields.title ? mapMarkers[dialogIndex].fields.certificate.fields.title : mapMarkers[dialogIndex].fields.certificate.fields.url}
                            </Link>
                        </div>
                    }

                    {mapMarkers[dialogIndex].fields.cooperation &&
                        <div className="flex mb-s mt-m">
                            <IconRenderer icon="icon-heart-handshake" className="" FillColor="black"/>
                            <p className="ml-s">{calculateCooperationYear(mapMarkers[dialogIndex].fields.cooperation)}</p>
                        </div>
                    }
                </dialog>
            </Column>
        </Container>
    )
}

export default DynamicMap;
